/**
 * Touch Panel deployment preparation service (Phase 4).
 * Creates immutable prepared records — does NOT publish MQTT.
 *
 * Collection: tenants/{tenantId}/touchPanelDeployments/{deploymentId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';
import { createLocalId } from '../pages/Admin/TouchPanelDesigner/panelModel';
import { getTouchPanel } from './touchPanelService';
import {
  listAssignmentsForPanel,
  markAssignmentsPrepared,
  listEligibleTouchPanelDevices,
} from './touchPanelAssignmentService';
import { serializeDeployablePanel, summarizePayload } from './touchPanelDeployable/serializeDeployablePanel';
import { validateDeployablePanel } from './touchPanelDeployable/validateDeployablePanel';

export const TOUCH_PANEL_DEPLOYMENTS_COLLECTION = 'touchPanelDeployments';

function ensureTenantId(tenantId) {
  if (!tenantId || !String(tenantId).trim()) {
    throw new Error('tenantId is required');
  }
  return String(tenantId).trim();
}

function nowIso() {
  return new Date().toISOString();
}

function deploymentsCollection(tenantId) {
  return collection(
    db,
    'tenants',
    ensureTenantId(tenantId),
    TOUCH_PANEL_DEPLOYMENTS_COLLECTION
  );
}

function deploymentDocRef(tenantId, deploymentId) {
  return doc(
    db,
    'tenants',
    ensureTenantId(tenantId),
    TOUCH_PANEL_DEPLOYMENTS_COLLECTION,
    deploymentId
  );
}

export function mapDeploymentDoc(snapshot) {
  if (!snapshot?.exists()) return null;
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    tenantId: data.tenantId || null,
    panelId: data.panelId || null,
    panelRevision: data.panelRevision == null ? null : Number(data.panelRevision),
    createdAt: data.createdAt || null,
    createdBy: data.createdBy || null,
    status: data.status || 'prepared',
    payloadSchemaVersion: data.payloadSchemaVersion ?? data.payload?.schemaVersion ?? 1,
    payloadSizeBytes: data.payloadSizeBytes ?? null,
    targetDeviceIds: Array.isArray(data.targetDeviceIds) ? data.targetDeviceIds : [],
    payload: data.payload || null,
    validation: data.validation || { valid: false, warnings: [], errors: [] },
    delivery: data.delivery || { attemptedAt: null, completedAt: null },
  };
}

export async function getTouchPanelDeployment(tenantId, deploymentId) {
  const snap = await getDoc(deploymentDocRef(tenantId, deploymentId));
  return mapDeploymentDoc(snap);
}

export async function listTouchPanelDeployments(tenantId, { panelId = null } = {}) {
  const tid = ensureTenantId(tenantId);
  let snap;
  if (panelId) {
    snap = await getDocs(
      query(deploymentsCollection(tid), where('panelId', '==', panelId))
    );
  } else {
    snap = await getDocs(query(deploymentsCollection(tid), orderBy('createdAt', 'desc')));
  }
  const rows = snap.docs.map((d) => mapDeploymentDoc(d)).filter(Boolean);
  return rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

/**
 * Build + validate deployable payload for a panel without writing.
 */
export async function previewDeployment(
  tenantId,
  { panelId, deviceIds = null, allowIncompatible = false } = {}
) {
  const tid = ensureTenantId(tenantId);
  const panel = await getTouchPanel(tid, panelId);
  if (!panel) throw new Error('Touch panel not found');

  const assignments = await listAssignmentsForPanel(tid, panelId);
  const targetIds =
    Array.isArray(deviceIds) && deviceIds.length > 0
      ? deviceIds
      : assignments.map((a) => a.deviceId);

  if (targetIds.length === 0) {
    throw new Error('No target devices selected or assigned');
  }

  const eligible = await listEligibleTouchPanelDevices(tid, { includeIncompatible: true });
  const byId = new Map(eligible.map((d) => [d.deviceId, d]));

  // Assigned devices missing from the paginated agent list (default API limit was 20)
  // used to surface as DEVICE_INCOMPATIBLE. Resolve them individually.
  const missingIds = targetIds.filter((id) => !byId.has(id));
  if (missingIds.length > 0) {
    const { fetchAgentById } = await import('../api/agentApi');
    const { toEligibleDeviceView } = await import('./touchPanelDeployable/eligibility');
    await Promise.all(
      missingIds.map(async (id) => {
        try {
          const agent = await fetchAgentById(id);
          if (agent) {
            byId.set(id, toEligibleDeviceView(agent));
          }
        } catch (err) {
          console.warn('[TouchPanelDeploy] fallback agent fetch failed', id, err);
        }
      })
    );
  }

  const deviceChecks = targetIds.map((id) => {
    const device = byId.get(id);
    if (!device) {
      return {
        deviceId: id,
        compatibilityStatus: 'incompatible',
        error: 'Device not found in agent list',
        notFound: true,
      };
    }
    if (device.compatibilityStatus === 'incompatible' && !allowIncompatible) {
      return {
        deviceId: id,
        name: device.name,
        compatibilityStatus: 'incompatible',
        error: 'Device incompatible',
        device,
      };
    }
    return {
      deviceId: id,
      name: device.name,
      compatibilityStatus: device.compatibilityStatus,
      device,
    };
  });

  const payload = serializeDeployablePanel(panel);
  const summary = summarizePayload(payload);

  // Validate once globally, then per-device for compatibility
  const baseValidation = validateDeployablePanel(payload);
  const perDeviceValidations = deviceChecks.map((check) => {
    const v = validateDeployablePanel(payload, { device: check.device || check });
    return { deviceId: check.deviceId, ...v, compatibilityStatus: check.compatibilityStatus };
  });

  const blockingDeviceErrors = perDeviceValidations.flatMap((v) =>
    (v.errors || []).map((e) => ({ ...e, deviceId: v.deviceId }))
  );

  const allErrors = [...baseValidation.errors, ...blockingDeviceErrors];
  // Dedupe
  const seen = new Set();
  const errors = [];
  allErrors.forEach((e) => {
    const key = `${e.code}|${e.path}|${e.message}|${e.deviceId || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    errors.push(e);
  });

  const warnings = [
    ...baseValidation.warnings,
    ...perDeviceValidations.flatMap((v) => v.warnings || []),
  ];

  return {
    panel,
    payload,
    summary,
    targetDeviceIds: targetIds,
    deviceChecks,
    validation: {
      valid: errors.length === 0,
      errors,
      warnings,
    },
  };
}

/**
 * Create an immutable prepared deployment record and mark assignments prepared.
 * Does not send MQTT. Does not mark deployed.
 */
export async function prepareTouchPanelDeployment(
  tenantId,
  {
    panelId,
    deviceIds = null,
    userId,
    allowIncompatible = false,
    confirmWarnings = false,
  } = {}
) {
  const tid = ensureTenantId(tenantId);
  const preview = await previewDeployment(tid, { panelId, deviceIds, allowIncompatible });

  if (!preview.validation.valid) {
    const err = new Error('Deployment validation failed');
    err.code = 'VALIDATION_FAILED';
    err.validation = preview.validation;
    err.preview = preview;
    throw err;
  }

  if (preview.validation.warnings.length > 0 && !confirmWarnings) {
    const err = new Error('Deployment has warnings; confirm to continue');
    err.code = 'WARNINGS_REQUIRE_CONFIRMATION';
    err.validation = preview.validation;
    err.preview = preview;
    throw err;
  }

  const deploymentId = createLocalId('tpdeploy');
  const iso = nowIso();
  const record = {
    id: deploymentId,
    tenantId: tid,
    panelId: preview.panel.id,
    panelRevision: Number(preview.panel.revision) || 1,
    createdAt: iso,
    createdBy: userId || null,
    status: 'prepared',
    payloadSchemaVersion: preview.payload.schemaVersion,
    payloadSizeBytes: preview.summary.sizeBytes,
    targetDeviceIds: preview.targetDeviceIds,
    payload: preview.payload,
    validation: {
      valid: true,
      warnings: preview.validation.warnings,
      errors: [],
    },
    delivery: {
      attemptedAt: null,
      completedAt: null,
    },
  };

  // Create-only — never overwrite an existing deployment id
  const existing = await getDoc(deploymentDocRef(tid, deploymentId));
  if (existing.exists()) {
    throw new Error('Deployment id collision; retry');
  }

  await setDoc(deploymentDocRef(tid, deploymentId), record);

  await markAssignmentsPrepared(tid, {
    panelId: preview.panel.id,
    panelRevision: record.panelRevision,
    deviceIds: preview.targetDeviceIds,
    deploymentId,
    userId,
  });

  return record;
}
